"use strict";
/**
 * 统一网络配置管理
 * 集中管理所有微服务的网络配置，避免配置分散和重复
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateNetworkConfig = exports.getServiceConfig = exports.getNetworkConfig = exports.NetworkConfigManager = void 0;
// 微服务配置定义
const MICROSERVICE_CONFIGS = {
    msLogin: {
        name: 'msLogin',
        displayName: '认证服务',
        description: '用户认证和权限管理服务',
        version: '1.0.0',
        frontendPort: 30090,
        backendPort: 50090,
        domain: {
            development: 'localhost',
            production: 'login.ihoms.cn'
        },
        api: {
            prefix: '/api',
            timeout: 15000,
            retryCount: 3,
            retryDelay: 1000
        },
        dependencies: []
    },
    msAdmin: {
        name: 'msAdmin',
        displayName: '管理服务',
        description: '系统管理和配置服务',
        version: '1.0.0',
        frontendPort: 30091,
        backendPort: 50091,
        domain: {
            development: 'localhost',
            production: 'admin.ihoms.cn'
        },
        api: {
            prefix: '/api',
            timeout: 15000,
            retryCount: 3,
            retryDelay: 1000
        },
        dependencies: ['msLogin']
    },
    msLog: {
        name: 'msLog',
        displayName: '日志服务',
        description: '系统日志管理和查询服务',
        version: '1.0.0',
        frontendPort: 30034,
        backendPort: 50034,
        domain: {
            development: 'localhost',
            production: 'log.ihoms.cn'
        },
        api: {
            prefix: '/api',
            timeout: 15000,
            retryCount: 3,
            retryDelay: 1000
        },
        dependencies: ['msLogin']
    },
    msMq: {
        name: 'msMq',
        displayName: '消息队列服务',
        description: '消息队列和任务处理服务',
        version: '1.0.0',
        frontendPort: 30032,
        backendPort: 50032,
        domain: {
            development: 'localhost',
            production: 'mq.ihoms.cn'
        },
        api: {
            prefix: '/api',
            timeout: 15000,
            retryCount: 3,
            retryDelay: 1000
        },
        dependencies: ['msLogin', 'msLog']
    },
    msSpace: {
        name: 'msSpace',
        displayName: '存储服务',
        description: '文件存储和空间管理服务',
        version: '1.0.0',
        frontendPort: 30035,
        backendPort: 50035,
        domain: {
            development: 'localhost',
            production: 'space.ihoms.cn'
        },
        api: {
            prefix: '/api',
            timeout: 15000,
            retryCount: 3,
            retryDelay: 1000
        },
        dependencies: ['msLogin']
    },
    msStun: {
        name: 'msStun',
        displayName: 'STUN服务',
        description: 'WebRTC STUN/TURN 服务',
        version: '1.0.0',
        frontendPort: 30031,
        backendPort: 50031,
        domain: {
            development: 'localhost',
            production: 'stun.ihoms.cn'
        },
        api: {
            prefix: '/api',
            timeout: 15000,
            retryCount: 3,
            retryDelay: 1000
        },
        dependencies: ['msLogin']
    },
    msAi: {
        name: 'msAi',
        displayName: 'AI 资源管理服务',
        description: 'AI 资源管理和统一调用服务',
        version: '1.0.0',
        frontendPort: 30083,
        backendPort: 50083,
        domain: {
            development: 'localhost',
            production: 'ai.ihoms.cn'
        },
        api: {
            prefix: '/api',
            timeout: 30000,
            retryCount: 3,
            retryDelay: 1000
        },
        dependencies: ['msLogin']
    },
    msDevice: {
        name: 'msDevice',
        displayName: '设备管理服务',
        description: '设备管理和监控服务',
        version: '1.0.0',
        frontendPort: 30041,
        backendPort: 50041,
        domain: {
            development: 'localhost',
            production: 'device.ihoms.cn'
        },
        api: {
            prefix: '/api',
            timeout: 15000,
            retryCount: 3,
            retryDelay: 1000
        },
        dependencies: ['msLogin', 'msSpace']
    },
    msOrg: {
        name: 'msOrg',
        displayName: '组织及用户服务',
        description: '组织结构与成员、应用与角色权限',
        version: '1.0.0',
        frontendPort: 30023,
        backendPort: 50023,
        domain: {
            development: 'localhost',
            production: 'org.ihoms.cn'
        },
        api: {
            prefix: '/api',
            timeout: 15000,
            retryCount: 3,
            retryDelay: 1000
        },
        dependencies: ['msLogin']
    }
};
class NetworkConfigManager {
    constructor() {
        this.hasWarnedMissingService = false;
        this.config = this.buildConfig();
    }
    static getInstance() {
        if (!NetworkConfigManager.instance) {
            NetworkConfigManager.instance = new NetworkConfigManager();
        }
        return NetworkConfigManager.instance;
    }
    setServiceName(serviceName) {
        if (!serviceName) {
            return;
        }
        if (this.forcedServiceName === serviceName) {
            return;
        }
        this.forcedServiceName = serviceName;
        this.hasWarnedMissingService = false;
        this.rebuild();
    }
    buildConfig() {
        // 在浏览器中，通过 window.location 判断环境
        let env;
        // 类型安全的 window 检查（使用全局变量检查）
        const globalWindow = typeof globalThis !== 'undefined' ? globalThis.window : undefined;
        const isBrowser = globalWindow !== undefined && typeof globalWindow.location !== 'undefined';
        if (isBrowser) {
            // 浏览器环境：根据 hostname 判断
            const hostname = globalWindow.location.hostname;
            env = (hostname === 'localhost' || hostname === '127.0.0.1') ? 'development' : 'production';
        }
        else {
            // Node.js 环境：使用 process.env
            env = (typeof process !== 'undefined' && process.env?.NODE_ENV) || 'development';
        }
        const isDevelopment = env === 'development';
        const isProduction = env === 'production';
        // 获取当前服务配置
        const currentService = this.getCurrentService();
        if (!currentService) {
            console.warn('⚠️ 无法确定当前微服务，使用默认配置');
            // 使用默认配置而不是抛出错误
            const defaultService = {
                name: 'mslogin',
                displayName: '微服务认证中心',
                description: '统一的微服务认证和授权中心',
                version: '1.0.0',
                frontendPort: 30090,
                backendPort: 50090,
                domain: {
                    development: 'localhost',
                    production: (() => {
                        const globalWindow = typeof globalThis !== 'undefined' ? globalThis.window : undefined;
                        return (globalWindow !== undefined && typeof globalWindow.location !== 'undefined') ? globalWindow.location.hostname : 'login.ihoms.cn';
                    })()
                },
                api: {
                    prefix: '/api',
                    timeout: 30000,
                    retryCount: 3,
                    retryDelay: 1000
                },
                dependencies: []
            };
            return this.buildConfigFromService(defaultService, isDevelopment, isProduction);
        }
        return this.buildConfigFromService(currentService, isDevelopment, isProduction);
    }
    buildConfigFromService(service, isDevelopment, isProduction) {
        // 构建 API 基础 URL
        const apiBaseUrl = this.buildApiBaseUrl(service, isDevelopment);
        // 构建 CORS 源列表
        const corsOrigins = this.buildCorsOrigins(isDevelopment);
        // 构建 msLogin URL
        const msLoginUrl = this.buildMsLoginUrl(isDevelopment);
        // 构建其他服务地址
        const services = this.buildServicesConfig(isDevelopment);
        return {
            apiBaseUrl,
            apiPrefix: service.api.prefix,
            timeout: service.api.timeout,
            retryCount: service.api.retryCount,
            retryDelay: service.api.retryDelay,
            corsOrigins,
            msLoginUrl,
            services,
            isDevelopment,
            isProduction,
            debug: isDevelopment
        };
    }
    getCurrentService() {
        if (this.forcedServiceName) {
            // 先尝试直接匹配（驼峰式）
            let forcedConfig = MICROSERVICE_CONFIGS[this.forcedServiceName];
            // 如果直接匹配失败，尝试大小写不敏感匹配
            if (!forcedConfig) {
                // 尝试匹配所有可能的变体（大小写不敏感）
                const normalizedForcedName = this.forcedServiceName.toLowerCase();
                const possibleKeys = Object.keys(MICROSERVICE_CONFIGS).filter(key => key.toLowerCase() === normalizedForcedName);
                if (possibleKeys.length > 0) {
                    forcedConfig = MICROSERVICE_CONFIGS[possibleKeys[0]];
                    // 如果通过大小写不敏感匹配找到了，静默使用，不输出警告
                    // 同时更新 forcedServiceName 为正确的键名，避免后续重复匹配
                    this.forcedServiceName = possibleKeys[0];
                    return forcedConfig;
                }
            }
            if (forcedConfig) {
                return forcedConfig;
            }
            // 只有在真正找不到匹配时才输出警告（大小写不敏感匹配也失败）
            if (!this.hasWarnedMissingService) {
                this.hasWarnedMissingService = true;
                console.warn(`⚠️ 无法找到强制指定的微服务配置: ${this.forcedServiceName}，将使用默认配置`);
            }
        }
        // 安全地读取环境变量（支持 Vite 的 define 替换和直接访问）
        let serviceName;
        try {
            if (typeof process !== 'undefined' && process.env) {
                serviceName = process.env.SERVICE_NAME || process.env.REACT_APP_SERVICE_NAME;
            }
        }
        catch (e) {
            // 忽略错误，继续尝试其他方式
        }
        // 如果通过 process.env 无法获取，尝试从全局变量获取（Vite define 可能创建了全局变量）
        if (!serviceName) {
            try {
                // @ts-ignore - 这些变量可能通过 Vite define 注入
                const globalProcess = (typeof globalThis !== 'undefined' && globalThis.process) ||
                    (typeof window !== 'undefined' && window.process);
                if (globalProcess && globalProcess.env) {
                    serviceName = globalProcess.env.SERVICE_NAME || globalProcess.env.REACT_APP_SERVICE_NAME;
                }
            }
            catch (e) {
                // 忽略错误
            }
        }
        if (serviceName) {
            // 先尝试直接匹配
            let config = MICROSERVICE_CONFIGS[serviceName];
            // 如果直接匹配失败，尝试大小写不敏感匹配
            if (!config) {
                const normalizedServiceName = serviceName.toLowerCase();
                const possibleKeys = Object.keys(MICROSERVICE_CONFIGS).filter(key => key.toLowerCase() === normalizedServiceName);
                if (possibleKeys.length > 0) {
                    config = MICROSERVICE_CONFIGS[possibleKeys[0]];
                    // 静默使用匹配到的配置，不输出警告
                }
            }
            return config || null;
        }
        const detectedService = this.detectServiceFromEnvironment();
        if (detectedService) {
            return detectedService;
        }
        if (!this.hasWarnedMissingService) {
            this.hasWarnedMissingService = true;
            // 已移除警告打印
        }
        // 返回默认配置而不是抛出错误
        return {
            name: 'mslogin',
            displayName: '微服务认证中心',
            description: '统一的微服务认证和授权中心',
            version: '1.0.0',
            frontendPort: 30090,
            backendPort: 50090,
            domain: {
                development: 'localhost',
                production: (() => {
                    const globalWindow = typeof globalThis !== 'undefined' ? globalThis.window : undefined;
                    return (globalWindow !== undefined && typeof globalWindow.location !== 'undefined') ? globalWindow.location.hostname : 'login.ihoms.cn';
                })()
            },
            api: {
                prefix: '/api',
                timeout: 30000,
                retryCount: 3,
                retryDelay: 1000
            },
            dependencies: []
        };
    }
    detectServiceFromEnvironment() {
        const services = Object.values(MICROSERVICE_CONFIGS);
        const globalWindow = typeof globalThis !== 'undefined' ? globalThis.window : undefined;
        if (globalWindow && typeof globalWindow.location !== 'undefined') {
            const { hostname, port } = globalWindow.location;
            const normalizedHost = hostname?.toLowerCase();
            if (normalizedHost) {
                const matchedByDomain = services.find((service) => [service.domain.development, service.domain.production]
                    .filter(Boolean)
                    .map((domain) => domain.toLowerCase())
                    .includes(normalizedHost));
                if (matchedByDomain) {
                    return matchedByDomain;
                }
            }
            if (port) {
                const matchedByPort = services.find((service) => String(service.frontendPort) === port || String(service.backendPort) === port);
                if (matchedByPort) {
                    return matchedByPort;
                }
            }
        }
        if (typeof process !== 'undefined') {
            const runtimeHost = process.env?.HOSTNAME?.toLowerCase();
            if (runtimeHost) {
                const matchedByHost = services.find((service) => [service.domain.development, service.domain.production]
                    .filter(Boolean)
                    .map((domain) => domain.toLowerCase())
                    .includes(runtimeHost));
                if (matchedByHost) {
                    return matchedByHost;
                }
            }
            const runtimePort = process.env?.PORT;
            if (runtimePort) {
                const matchedByRuntimePort = services.find((service) => String(service.frontendPort) === runtimePort || String(service.backendPort) === runtimePort);
                if (matchedByRuntimePort) {
                    return matchedByRuntimePort;
                }
            }
        }
        return null;
    }
    buildApiBaseUrl(service, isDevelopment) {
        // 在浏览器环境中，根据运行域名判断是否需要使用相对路径
        const globalWindow = typeof globalThis !== 'undefined' ? globalThis.window : undefined;
        const isBrowser = globalWindow !== undefined && typeof globalWindow.location !== 'undefined';
        if (isBrowser) {
            const currentHost = globalWindow.location.hostname?.toLowerCase() || '';
            const currentPort = globalWindow.location.port;
            const candidateHosts = [
                service.domain.development?.toLowerCase(),
                service.domain.production?.toLowerCase(),
            ].filter(Boolean);
            const isServiceHost = candidateHosts.includes(currentHost);
            const matchesFrontendPort = currentPort && String(service.frontendPort) === currentPort;
            const isSameFrontendInstance = isServiceHost && (!currentPort || currentPort === '' || matchesFrontendPort);
            // 当直接访问服务域名或端口时仍然使用相对路径，交由网关处理
            if (isSameFrontendInstance) {
                return '';
            }
            // 如果当前域名不是服务自身域名，意味着可能被其他微服务 iframe 引用，需显式指向服务后端
            const protocol = globalWindow.location.protocol || (isDevelopment ? 'http:' : 'https:');
            const domain = isDevelopment ? service.domain.development : service.domain.production;
            const port = isDevelopment ? `:${service.backendPort}` : '';
            return `${protocol}//${domain}${port}`;
        }
        // Node.js 环境：优先使用环境变量
        const envApiUrl = (typeof process !== 'undefined' && (process.env?.REACT_APP_API_URL || process.env?.REACT_APP_API_BASE_URL)) || undefined;
        if (envApiUrl) {
            return envApiUrl;
        }
        // 根据环境构建 URL
        const protocol = isDevelopment ? 'http' : 'https';
        const domain = isDevelopment ? service.domain.development : service.domain.production;
        const port = isDevelopment ? `:${service.backendPort}` : '';
        const url = `${protocol}://${domain}${port}`;
        // 生产环境验证
        if (!isDevelopment && url.includes('localhost')) {
            throw new Error(`🚨 生产环境不能使用 localhost，请配置 ${service.name} 的环境变量`);
        }
        return url;
    }
    buildCorsOrigins(isDevelopment) {
        const origins = [];
        // 添加所有微服务的前端地址
        Object.values(MICROSERVICE_CONFIGS).forEach(service => {
            const protocol = isDevelopment ? 'http' : 'https';
            const domain = isDevelopment ? service.domain.development : service.domain.production;
            const port = isDevelopment ? `:${service.frontendPort}` : '';
            origins.push(`${protocol}://${domain}${port}`);
        });
        // 添加主域名
        origins.push('http://ihoms.cn', 'https://ihoms.cn');
        // 开发环境添加常用端口
        if (isDevelopment) {
            origins.push('http://localhost:3000', 'http://localhost:3001', 'http://localhost:5173', 'http://127.0.0.1:3000', 'http://127.0.0.1:3001', 'http://127.0.0.1:5173');
        }
        return [...new Set(origins)]; // 去重
    }
    buildMsLoginUrl(isDevelopment) {
        const envMsLoginUrl = (typeof process !== 'undefined' && (process.env?.REACT_APP_MSLOGIN_URL || process.env?.MSLOGIN_API_URL)) || undefined;
        if (envMsLoginUrl) {
            return envMsLoginUrl;
        }
        const msLogin = MICROSERVICE_CONFIGS.msLogin;
        const protocol = isDevelopment ? 'http' : 'https';
        const domain = isDevelopment ? msLogin.domain.development : msLogin.domain.production;
        const port = isDevelopment ? `:${msLogin.backendPort}` : '';
        return `${protocol}://${domain}${port}`;
    }
    buildServicesConfig(isDevelopment) {
        const services = {};
        Object.values(MICROSERVICE_CONFIGS).forEach(service => {
            if (service.name === 'msLogin')
                return; // msLogin 单独处理
            const protocol = isDevelopment ? 'http' : 'https';
            const domain = isDevelopment ? service.domain.development : service.domain.production;
            const port = isDevelopment ? `:${service.backendPort}` : '';
            services[service.name] = `${protocol}://${domain}${port}`;
        });
        return services;
    }
    // 获取配置
    getConfig() {
        return { ...this.config };
    }
    // 获取特定服务的配置
    getServiceConfig(serviceName) {
        // 先尝试直接匹配
        let config = MICROSERVICE_CONFIGS[serviceName];
        // 如果直接匹配失败，尝试大小写不敏感匹配
        if (!config) {
            const possibleKeys = Object.keys(MICROSERVICE_CONFIGS).filter(key => key.toLowerCase() === serviceName.toLowerCase());
            if (possibleKeys.length > 0) {
                config = MICROSERVICE_CONFIGS[possibleKeys[0]];
            }
        }
        return config || null;
    }
    // 获取所有服务配置
    getAllServiceConfigs() {
        return { ...MICROSERVICE_CONFIGS };
    }
    // 验证配置
    validateConfig() {
        const errors = [];
        if (!this.config.apiBaseUrl) {
            errors.push('API 基础 URL 未配置');
        }
        if (!this.config.msLoginUrl) {
            errors.push('msLogin URL 未配置');
        }
        if (this.config.isProduction && this.config.apiBaseUrl.includes('localhost')) {
            errors.push('生产环境不能使用 localhost');
        }
        if (this.config.corsOrigins.length === 0) {
            errors.push('CORS 源列表为空');
        }
        return {
            valid: errors.length === 0,
            errors
        };
    }
    // 重新构建配置（用于环境变量更新后）
    rebuild() {
        this.config = this.buildConfig();
    }
}
exports.NetworkConfigManager = NetworkConfigManager;
// 延迟初始化的单例实例
let _networkConfig = null;
const getNetworkConfigInstance = () => {
    if (!_networkConfig) {
        _networkConfig = NetworkConfigManager.getInstance();
    }
    return _networkConfig;
};
// 便捷导出
const getNetworkConfig = (serviceName) => {
    const manager = getNetworkConfigInstance();
    if (serviceName) {
        manager.setServiceName(serviceName);
    }
    return manager.getConfig();
};
exports.getNetworkConfig = getNetworkConfig;
const getServiceConfig = (serviceName) => getNetworkConfigInstance().getServiceConfig(serviceName);
exports.getServiceConfig = getServiceConfig;
const validateNetworkConfig = () => getNetworkConfigInstance().validateConfig();
exports.validateNetworkConfig = validateNetworkConfig;
